import { Button, Popconfirm, Tooltip } from 'antd';
import { Link } from 'react-router';
import { accountTabPath } from '../../account/account.paths';
import type { UserListItem } from '../users.api';

interface UserActionsProps {
  user: UserListItem;
  /** Your own page: the server refuses self-deactivation, so the button explains why rather than failing. */
  isYou: boolean;
  /**
   * Acting as someone needs more than the caller's password: a code sign-in or two-factor turned on (the server refuses
   * otherwise), so the button explains that up front.
   */
  hasSecondFactor: boolean;
  /** Undefined unless the caller holds `user.impersonate`; the server refuses anyone else. */
  onActAs?: (user: UserListItem) => Promise<void>;
  onDeactivate: () => Promise<void>;
  onReactivate: () => Promise<void>;
}

function ActAsButton({ user, hasSecondFactor, onActAs }: { user: UserListItem; hasSecondFactor: boolean; onActAs: (user: UserListItem) => Promise<void> }) {
  if (!hasSecondFactor) {
    return (
      <Tooltip
        title={
          <>
            Sign in with a code, or turn on two-factor sign-in in <Link to={accountTabPath('security')}>your security settings</Link>, before acting as another
            user.
          </>
        }
      >
        {/* A disabled button swallows hover, so the tooltip hangs off a wrapper. */}
        <span>
          <Button disabled>Act as</Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <Popconfirm
      title={`Act as ${user.name}?`}
      description="You'll see the app as they do. Your actions are recorded under your name."
      onConfirm={() => onActAs(user)}
    >
      <Button>Act as</Button>
    </Popconfirm>
  );
}

function StatusButton({ user, isYou, onDeactivate, onReactivate }: Pick<UserActionsProps, 'user' | 'isYou' | 'onDeactivate' | 'onReactivate'>) {
  if (isYou) {
    return (
      <Tooltip title="You can't deactivate your own account">
        <Button danger disabled>
          Deactivate
        </Button>
      </Tooltip>
    );
  }
  if (user.isActive) {
    return (
      <Popconfirm title={`Deactivate ${user.name}?`} description="They are signed out everywhere immediately." onConfirm={onDeactivate}>
        <Button danger>Deactivate</Button>
      </Popconfirm>
    );
  }
  return (
    <Popconfirm title={`Reactivate ${user.name}?`} onConfirm={onReactivate}>
      <Button>Reactivate</Button>
    </Popconfirm>
  );
}

/** Deactivation is immediate and affects someone else's account, so it asks first — same weight CLAUDE.md gives closing out a trip. */
export function UserActions({ user, isYou, hasSecondFactor, onActAs, onDeactivate, onReactivate }: UserActionsProps) {
  const canActAs = onActAs !== undefined && !isYou && user.isActive && !user.roles.includes('admin');

  return (
    <span className="inline-flex flex-wrap gap-2">
      {canActAs && <ActAsButton user={user} hasSecondFactor={hasSecondFactor} onActAs={onActAs} />}
      <StatusButton user={user} isYou={isYou} onDeactivate={onDeactivate} onReactivate={onReactivate} />
    </span>
  );
}
